// @ts-nocheck

import type http from "http";
import cuid from "cuid";

import type { NotificationFeedPreference } from "@storyboard/web-client";
import { AccountEvent as AccountEventClient } from "./api/AccountEvent";
import { AccountEvents as AccountEventsClient } from "./api/AccountEvents";
import { Bootstrap as BootstrapClient } from "./api/Bootstrap";
import { Call as CallClient } from "./api/Call";
import { Content as ContentClient } from "./api/Content";
import { Device as DeviceClient } from "./api/Device";
import type {
  AccountEventRequest,
  AccountEventRequests,
  AppContext,
  CallStatus,
  ContentEventStep,
  ContentEventStepStatus,
  ContentLanguageRequest,
  CreateContentEventRequests,
  CreatePingResponse,
  DeviceContext,
  DeviceRegistrationRequest,
  HandsFreeStatusRequest,
  HeartbeatRequest,
  LocationsRequest,
  SearchFeedsRequest,
  SearchFeedsResponse,
  SearchMessageResponse,
  SearchMessagesRequest,
  UploadDeviceLogs,
  WorkspaceInvitation,
  WorkspaceRole,
} from "./api/data-contracts";
import { Echo as EchoClient } from "./api/Echo";
import type { HttpResponse, RequestParams } from "./api/http-client";
import { Logs as LogsClient } from "./api/Logs";
import { WorkspaceId as SearchClient } from "./api/WorkspaceId";
import { Workspaces as WorkspacesClient } from "./api/Workspaces";
import { Accounts as AccountsClient } from "./auth/Accounts";
import { Login } from "./auth/Login";
import { Userinfo as UserInfoClient } from "./auth/Userinfo";

// use math.random to generate a uuid
const defaultIdGenerator = () => {
  return cuid();
};

export default class Client {
  public apiBaseUrl: string;
  public authBaseUrl: string;

  public userInfoClient: UserInfoClient;
  public contentClient: ContentClient;
  public accountEventClient: AccountEventClient;
  public accountEventsClient: AccountEventsClient;
  public loginClient: Login;
  public accountClients: AccountsClient;
  public workspacesClient: WorkspacesClient;
  public searchClient: SearchClient;
  public bootstrapClient: BootstrapClient;
  public logsClient: LogsClient;
  public echoClient: EchoClient;
  public deviceClient: DeviceClient;
  public callClient: CallClient;
  public baseDomain: string;
  public login?: () => void;
  public logout?: (returnTo?: string) => void;
  public logoutLink?: (returnTo?: string) => string;
  public headers?: Record<string, string>;
  public eventMap: Map<string, Array<(value: any) => void>>;
  public generateId: () => string;
  public appContext: AppContext;
  public deviceContext: DeviceContext;

  constructor({
    baseDomain,
    headers,
    login,
    logout,
    logoutLink,
    generateId,
    agent,
  }: {
    baseDomain?: string;
    headers?: Record<string, string>;
    login?: () => void;
    logout?: (returnTo?: string) => void;
    logoutLink?: (returnTo?: string) => string;
    generateId?: () => string;
    agent?: http.Agent;
  }) {
    this.appContext = {};
    this.deviceContext = {};
    this.eventMap = new Map();
    this.headers = headers;
    this.baseDomain = baseDomain || "https://dev.app.storyboardtesting.com";
    this.generateId = generateId || defaultIdGenerator;

    this.login = login;
    this.logout = logout;
    this.logoutLink = logoutLink;

    this.apiBaseUrl = `${this.baseDomain}/v1`;
    this.authBaseUrl = `${this.baseDomain}/auth`;

    // Define client option types to include optional customFetch
    type ClientOptions = {
      baseUrl: string;
      baseApiParams: {
        credentials: RequestCredentials;
        headers?: Record<string, string>;
      };
      customFetch?: (url: string, init: RequestInit) => Promise<Response>;
    };

    const apiClientOptions: ClientOptions = {
      baseUrl: this.apiBaseUrl,
      baseApiParams: { credentials: "include" as RequestCredentials, headers },
    };

    const authClientOptions: ClientOptions = {
      baseUrl: this.authBaseUrl,
      baseApiParams: { credentials: "include" as RequestCredentials, headers },
    };

    // Only define customFetch if an agent is explicitly passed in
    if (agent) {
      apiClientOptions.customFetch = (url: string, init: RequestInit) => {
        // Merge headers from apiClientOptions.baseApiParams and from init
        const mergedHeaders = {
          ...((apiClientOptions.baseApiParams && apiClientOptions.baseApiParams.headers) || {}),
          ...(init.headers || {}),
        };
        return fetch(url, { ...init, headers: mergedHeaders, agent });
      };

      authClientOptions.customFetch = (url: string, init: RequestInit) => {
        // Merge headers from authClientOptions.baseApiParams and from init
        const mergedHeaders = {
          ...((authClientOptions.baseApiParams && authClientOptions.baseApiParams.headers) || {}),
          ...(init.headers || {}),
        };
        return fetch(url, { ...init, headers: mergedHeaders, agent });
      };
    }

    this.userInfoClient = new UserInfoClient(authClientOptions);
    this.accountClients = new AccountsClient(authClientOptions);
    this.loginClient = new Login(authClientOptions);

    this.contentClient = new ContentClient(apiClientOptions);
    this.accountEventClient = new AccountEventClient(apiClientOptions);
    this.accountEventsClient = new AccountEventsClient(apiClientOptions);
    this.workspacesClient = new WorkspacesClient(apiClientOptions);
    this.bootstrapClient = new BootstrapClient(apiClientOptions);
    this.logsClient = new LogsClient(apiClientOptions);
    this.echoClient = new EchoClient(apiClientOptions);
    this.searchClient = new SearchClient(apiClientOptions);
    this.deviceClient = new DeviceClient(apiClientOptions);
    this.callClient = new CallClient(apiClientOptions);
  }

  on(event: string, callback: (value: any) => void) {
    if (!this.eventMap.has(event)) {
      this.eventMap.set(event, []);
    }
    this.eventMap.get(event).push(callback);
  }

  remove(event: string, callback: (value: any) => void) {
    if (this.eventMap.has(event)) {
      const callbacks = this.eventMap.get(event).filter((cb) => cb !== callback);
      this.eventMap.set(event, callbacks);
    }
  }
  emit(event: string, ...data: any) {
    if (this.eventMap.has(event)) {
      this.eventMap.get(event).forEach((callback) => {
        callback(...data);
      });
    }
  }

  public async createScheduledBroadcast({
    workspaceId,
    feedIds,
    workspaceMembershipIds,
    workflowItemId,
    date,
    cron,
    timezone,
  }: {
    workspaceId: string;
    feedIds?: string[];
    workspaceMembershipIds?: string[];
    workflowItemId: string;
    date?: string;
    cron?: string;
    timezone?: string;
  }) {
    return this.processRequest(
      this.workspacesClient.triggerScheduleCreate(workspaceId, {
        feedIds,
        workspaceMembershipIds,
        workflowItemId,
        workspaceId,
        date,
        cron,
        timezone,
      }),
    );
  }

  public async updateScheduledBroadcast({
    workspaceId,
    scheduleId,
    feedIds,
    workspaceMembershipIds,
    date,
    cron,
    timezone,
  }: {
    workspaceId: string;
    scheduleId: string;
    feedIds?: string[];
    workspaceMembershipIds?: string[];
    date?: string;
    cron?: string;
    timezone?: string;
  }) {
    return this.processRequest(
      this.workspacesClient.triggerScheduleUpdate(workspaceId, scheduleId, {
        feedIds,
        workspaceMembershipIds,
        date,
        cron,
        timezone,
      }),
    );
  }

  public async getScheduledBroadcasts(workspaceId: string) {
    return this.processRequest(this.workspacesClient.scheduledBroadcastsDetail(workspaceId));
  }

  public async publishBroadcast({
    workspaceId,
    contentId,
    workspaceMembershipIds,
    feedIds,
    inputText,
  }: {
    workspaceId: string;
    contentId: string;
    workspaceMembershipIds?: string[];
    feedIds?: string[];
    inputText?: string;
  }) {
    return this.processRequest(
      this.workspacesClient.publishBroadcastCreate(workspaceId, {
        contentId,
        workspaceMembershipIds,
        feedIds,
        workspaceId,
        inputText,
      }),
    );
  }

  public async createWorkspaceWorkflowItem({
    workspaceId,
    contentId,
    displayName,
    workflowItemId,
    inputText,
  }: {
    workspaceId: string;
    contentId?: string;
    displayName?: string;
    workflowItemId?: string;
    inputText?: string;
  }) {
    contentId = contentId || this.generateId();
    workflowItemId = workflowItemId || this.generateId();
    return this.processRequest(
      this.workspacesClient.workflowItemsCreate(workspaceId, {
        workspaceId,
        contentId,
        displayName,
        workflowItemId,
        inputText,
      }),
    );
  }

  public async getWorkspaceWorkflowItems(workspaceId: string) {
    return this.processRequest(this.workspacesClient.workflowItemsDetail(workspaceId));
  }

  public async deleteWorkspaceWorkflowItem(workspaceId: string, workflowItemId: string) {
    return this.processRequest(this.workspacesClient.workflowItemsDelete(workspaceId, workflowItemId));
  }

  public async publishWorkspaceWorkflowItem({
    workspaceId,
    workflowItemId,
    feedIds = [],
    workspaceMembershipIds = [],
  }: {
    workspaceId: string;
    workflowItemId: string;
    feedIds?: string[];
    workspaceMembershipIds?: string[];
  }) {
    return this.processRequest(
      this.workspacesClient.workflowItemsPublishCreate(workspaceId, workflowItemId, {
        feedIds,
        workspaceMembershipIds,
      }),
    );
  }

  public async createWorkspaceInvitations(
    workspaceId: string,
    emails?: string[],
    phoneNumbers?: string[],
    sendEmail?: boolean,
    invitations?: WorkspaceInvitation[],
  ) {
    /**
     * Don't put all values into array, empty values will break the validator
     */
    const cleanupInvitations = invitations?.map((invitation) => {
      const newInvitation = {
        name: invitation.name,
        role: invitation.role,
        sendEmail: invitation.sendEmail,
      };
      if (invitation.email) {
        newInvitation.email = invitation.email;
      }
      if (invitation.phoneNumber) {
        newInvitation.phoneNumber = invitation.phoneNumber;
      }
      return newInvitation;
    });

    return this.processRequest(
      this.workspacesClient.directInvitationsCreate(workspaceId, {
        emails,
        phoneNumbers,
        sendEmail,
        invitations: cleanupInvitations,
      }),
    );
  }

  public async getWorkspaceInvitations(workspaceId: string) {
    return this.processRequest(this.workspacesClient.invitationsDetail(workspaceId));
  }

  public async deleteWorkspaceFeedItem(workspaceId: string, feedId: string, feedItemId: string) {
    return this.processRequest(this.workspacesClient.itemsDelete(workspaceId, feedId, feedItemId));
  }

  public async publishToWorkspaceFeed({
    workspaceId,
    feedId,
    contentId,
    itemId,
    groupId,
    url,
    text,
    preferredLanguage,
    isSilent,
  }: {
    workspaceId: string;
    feedId: string;
    contentId: string;
    itemId?: string;
    groupId?: string;
    url?: string;
    text?: string;
    preferredLanguage?: string;
    isSilent?: boolean;
  }) {
    itemId = itemId || this.generateId();

    return this.processRequest(
      this.workspacesClient.itemsCreate(workspaceId, feedId, {
        contentId,
        itemId,
        url,
        groupId,
        text,
        preferredLanguage,
        isSilent,
      }),
    );
  }

  public async publishTTS({
    workspaceId,
    feedId,
    contentId,
    itemId,
    groupId,
    text,
    preferredLanguage,
    isSilent,
  }: {
    workspaceId: string;
    feedId: string;
    contentId: string;
    itemId?: string;
    groupId?: string;
    text?: string;
    preferredLanguage?: string;
    isSilent?: boolean;
  }) {
    itemId = itemId || this.generateId();

    return this.processRequest(
      this.workspacesClient.postWorkspaces(workspaceId, feedId, {
        contentId,
        itemId,
        groupId,
        text,
        preferredLanguage,
        isSilent,
        appContext: this.appContext,
        deviceContext: this.deviceContext,
      }),
    );
  }

  public async getWorkspaceFeedEvents(workspaceId: string, feedId: string, page = 0, pageSize = 10000) {
    return this.processRequest(
      this.workspacesClient.feedsEventsDetail(workspaceId, feedId, {
        page,
        pageSize,
      }),
    );
  }

  public async getWorkspaceLatestFeedEvents(workspaceId: string, feedId: string, page = 0, pageSize = 10000) {
    return this.processRequest(
      this.workspacesClient.feedsLatestEventsDetail(workspaceId, feedId, {
        page,
        pageSize,
      }),
    );
  }

  public async getWorkspaceOldestFeedEvents(workspaceId: string, feedId: string, page = 0, pageSize = 10000) {
    return this.processRequest(
      this.workspacesClient.feedsOldestEventsDetail(workspaceId, feedId, {
        page,
        pageSize,
      }),
    );
  }

  public async getWorkspaceFeedPermissions({
    workspaceId,
    feedId,
    page = 0,
    pageSize = 1000,
  }: {
    workspaceId: string;
    feedId: string;
    page?: number;
    pageSize?: number;
  }) {
    return this.processRequest(
      this.workspacesClient.feedsPermissionsDetail(workspaceId, feedId, {
        page,
        pageSize,
      }),
    );
  }

  public async getWorkspaceFeedItems(workspaceId: string, feedId: string, page = 0, pageSize = 10) {
    return this.processRequest(
      this.workspacesClient.feedsItemsDetail(workspaceId, feedId, {
        page,
        pageSize,
      }),
    );
  }

  public async bootstrapWorkspaceFeedItems(workspaceId: string, page = 0, pageSize = 10000, timestamp?: string) {
    return this.processRequest(
      this.workspacesClient.feedItemsDetail(workspaceId, {
        page,
        pageSize,
        timestamp,
      }),
    );
  }

  public async getFeedItemContent(
    workspaceId: string,
    itemIds: string[],
    preferredLanguage?: string,
    timestamp?: string,
  ) {
    let language: ContentLanguageRequest;
    switch (preferredLanguage) {
      case "none":
        language = "original";
        break;
      default:
        language = preferredLanguage as ContentLanguageRequest;
    }
    return this.processRequest(
      this.workspacesClient.feedItemContentCreate(workspaceId, {
        itemIds,
        language,
        timestamp,
      }),
    );
  }

  public async getFeedItemOldestEvents(
    workspaceId: string,
    itemIds: string[],
    page = 0,
    pageSize = 1000,
    timestamp?: string,
  ) {
    return this.processRequest(
      this.workspacesClient.feedItemOldestEventsCreate(
        workspaceId,
        {
          itemIds,
        },
        { page, pageSize, timestamp },
      ),
    );
  }

  public async updateWorkspaceMember(workspaceId: string, workspaceMembershipIds: string[], role: WorkspaceRole) {
    return this.processRequest(
      this.workspacesClient.workspaceMembershipsUpdatePartialUpdate(workspaceId, {
        workspaceMembershipIds,
        role,
      }),
    );
  }

  public async removeWorkspaceMember(workspaceId: string, workspaceMembershipIds: string[]) {
    return this.processRequest(
      this.workspacesClient.workspaceMembershipsRemoveDelete(workspaceId, {
        workspaceMembershipIds,
        status: "inactive",
      }),
    );
  }

  public async bootstrapWorkspaces() {
    return this.processRequest(this.bootstrapClient.workspacesList());
  }

  public async tokenLogin(token: string) {
    return this.loginClient.tokenList({ token });
  }

  public async getUserInfo() {
    return this.processRequest(this.userInfoClient.userinfoList());
  }

  public async createContentEvent({
    contentId,
    workspaceId,
    step,
    status,
    context,
    feedId,
    error,
  }: {
    contentId: string;
    step: ContentEventStep;
    status: ContentEventStepStatus;
    workspaceId?: string;
    context?: object;
    feedId?: string;
    error?: Error;
  }) {
    return this.processRequest(
      this.contentClient.eventsCreate(contentId, {
        workspaceId,
        contentId,
        step,
        status,
        feedId,
        eventContext: context,
        timestamp: new Date().toISOString(),
        appContext: this.appContext,
        deviceContext: this.deviceContext,
        error,
      }),
    );
  }

  public async refreshContent(_uploadId?: string) {
    // uploadId = uploadId || this.generateId();
    // this.processRequest(this.contentClient.refreshCreate({ uploadId }));
    // return uploadId;
  }

  public async createAccountEvent(body: AccountEventRequest) {
    body.id = body.id || this.generateId();
    body.appContext = this.appContext;
    body.deviceContext = this.deviceContext;
    // console.log('account event', body);
    return this.processRequest(this.accountEventClient.accountEventCreate(body));
  }

  public async updateAccount(accountId: string, { fullName, email }: { fullName: string; email?: string }) {
    return this.processRequest(
      this.accountClients.accountsPartialUpdate(accountId, {
        name: fullName,
        email,
      }),
    );
  }

  public async setPushNotificationToken({
    accountId,
    deviceToken,
    friendlyName,
  }: {
    accountId: string;
    deviceToken: string;
    friendlyName: string;
  }) {
    return await this.processRequest(
      this.accountClients.pushnotificationdevicesUpdate(accountId, {
        enabled: true,
        surface: "web",
        deviceToken,
        friendlyName,
      }),
    );
  }

  public async deleteAccount(accountId: string) {
    await this.processRequest(this.accountClients.accountsDelete(accountId));
    return this.logout();
  }

  public async processRequest<I, T>(request: Promise<HttpResponse<I, T>>) {
    try {
      const response = await request;
      this.emit("success", true);
      if (response.status === 401) {
        console.log("401 - redirecting to login", response);
        if (this.login) {
          this.login();
        }
      }
      // The autogen HttpClient resolves successfully with `data = null` and
      // `error` populated when a 2xx response's body fails to parse (e.g.
      // connection truncation between headers and body, or browser-side
      // body interference). That looks identical to a legitimate empty
      // response downstream, which is how SVS-2478 surfaced as a misleading
      // "received an empty response" error. Surface a real Error here so
      // future occurrences land in Sentry with the underlying parse cause
      // instead of as a silent null. Falls through to the catch block
      // below so error logging and emit("errorCodes") happen in one place.
      //
      // Safe against legitimate empty/204 responses: the autogen client
      // only populates `r.error` from the body-parse `.catch`, which only
      // runs when the caller passed a `format` (e.g. "json"). 204-returning
      // endpoints (accountsDelete, templatesDelete, workflowItemsDelete,
      // pingResponseCreate, etc.) are generated as `request<void, any>`
      // with no `format`, so their body is never parsed and this branch
      // is unreachable for them.
      if (response.ok && response.data == null && response.error != null) {
        const parseError = new Error(`Response body parse failure (status ${response.status})`);
        (parseError as Error & { cause?: unknown }).cause = response.error;
        throw parseError;
      }
      return response.data;
    } catch (e) {
      this.emit("errorCodes", e);
      console.error("ERROR", e);
      switch (e.status) {
        case 401:
          console.log("401 from error - redirecting to login");
          if (this.login) {
            this.login();
          }
          break;
        case 500:
          throw new Error("Internal Server Error");
        default:
          return Promise.reject(e);
      }
    }
  }

  public async getWorkspaceTemplates({ workspaceId }: { workspaceId: string }) {
    return this.processRequest(this.workspacesClient.templatesDetail(workspaceId));
  }

  public async createWorkspaceTemplate({
    workspaceId,
    name,
    template,
  }: {
    workspaceId: string;
    name: string;
    template: string;
  }) {
    return this.processRequest(this.workspacesClient.templatesCreate(workspaceId, { name, template }));
  }

  public async updateWorkspaceTemplate({
    workspaceId,
    templateId,
    name,
    template,
  }: {
    workspaceId: string;
    templateId: string;
    name: string;
    template: string;
  }) {
    return this.processRequest(
      this.workspacesClient.templatesPartialUpdate(workspaceId, templateId, {
        name,
        template,
      }),
    );
  }

  public async deleteWorkspaceTemplate({ workspaceId, templateId }: { workspaceId: string; templateId: string }) {
    return this.processRequest(this.workspacesClient.templatesDelete(workspaceId, templateId));
  }

  public async uploadLogs({ logs, userMessage, suppressSlackNotification }: UploadDeviceLogs) {
    return this.processRequest(
      this.logsClient.logsCreate({
        deviceContext: this.deviceContext,
        appContext: this.appContext,
        logs,
        userMessage,
        suppressSlackNotification,
      }),
    );
  }

  public createContentEventBatch(contentId: string, request: CreateContentEventRequests) {
    return this.processRequest(this.contentClient.eventsBatchCreate(contentId, request));
  }

  public accountEventBatch(request: AccountEventRequests) {
    for (const event of request.data) {
      event.appContext = this.appContext;
      event.deviceContext = this.deviceContext;
    }
    return this.processRequest(this.accountEventsClient.accountEventsCreate(request));
  }

  public heartbeat(workspaceId: string, heartbeat: HeartbeatRequest) {
    heartbeat.appContext = heartbeat.appContext ?? this.appContext;
    heartbeat.deviceContext = heartbeat.deviceContext ?? this.deviceContext;
    return this.processRequest(this.workspacesClient.heartbeatCreate(workspaceId, heartbeat));
  }

  public getHandsFreeStatus(workspaceId: string, request: HandsFreeStatusRequest) {
    return this.processRequest(this.workspacesClient.handsfreestatusCreate(workspaceId, request));
  }

  public getLocations(workspaceId: string, request: LocationsRequest) {
    return this.processRequest(this.workspacesClient.locationsCreate(workspaceId, request));
  }

  public async asyncEcho(data: string) {
    try {
      const response = await this.processRequest(this.echoClient.asyncCreate({ data }));
      if (response?.data?.length > 0) {
        return response;
      }
      return Promise.reject("Failed Echo");
    } catch (e) {
      return Promise.reject(e);
    }
  }

  public searchMessages(
    workspaceId: string,
    body: SearchMessagesRequest,
    params?: RequestParams,
  ): Promise<SearchMessageResponse> {
    return this.processRequest(this.searchClient.messagesCreate(workspaceId, body, params));
  }

  public searchChannels(
    workspaceId: string,
    body: SearchFeedsRequest,
    params?: RequestParams,
  ): Promise<SearchFeedsResponse> {
    return this.processRequest(this.searchClient.feedsCreate(workspaceId, body, params));
  }

  public async registerDevice({
    deviceRegistration,
  }: {
    deviceRegistration: DeviceRegistrationRequest["deviceRegistration"];
  }) {
    return this.processRequest(
      this.deviceClient.registrationCreate({
        deviceRegistration,
      }),
    );
  }

  public async subscribeToFeed({
    workspaceId,
    feedId,
    workspaceMembershipId,
    preference,
  }: {
    workspaceId: string;
    feedId: string;
    workspaceMembershipId: string;
    preference: NotificationFeedPreference;
  }) {
    return this.processRequest(
      this.workspacesClient.feedsMembershipPreferencesPartialUpdate(workspaceId, feedId, workspaceMembershipId, {
        pushNotificationPreference: preference,
        handsFreeNotificationPreference: preference,
      }),
    );
  }

  public async unsubscribeFromFeed({
    workspaceId,
    feedId,
    workspaceMembershipId,
  }: {
    workspaceId: string;
    feedId: string;
    workspaceMembershipId: string;
  }) {
    return this.processRequest(
      this.workspacesClient.feedsMembershipPreferencesPartialUpdate(workspaceId, feedId, workspaceMembershipId, {
        pushNotificationPreference: "none",
        handsFreeNotificationPreference: "none",
      }),
    );
  }

  public async subscribeToFeedGroup({
    workspaceId,
    feedGroupId,
    workspaceMembershipId,
    preference,
  }: {
    workspaceId: string;
    feedGroupId: string;
    workspaceMembershipId: string;
    preference: NotificationFeedPreference;
  }) {
    return this.processRequest(
      this.workspacesClient.feedGroupsMembershipPreferencesPartialUpdate(
        workspaceId,
        feedGroupId,
        workspaceMembershipId,
        {
          pushNotificationPreference: preference,
          handsFreeNotificationPreference: preference,
        },
      ),
    );
  }

  public async unsubscribeFromFeedGroup({
    workspaceId,
    feedGroupId,
    workspaceMembershipId,
  }: {
    workspaceId: string;
    feedGroupId: string;
    workspaceMembershipId: string;
  }) {
    return this.processRequest(
      this.workspacesClient.feedGroupsMembershipPreferencesPartialUpdate(
        workspaceId,
        feedGroupId,
        workspaceMembershipId,
        {
          pushNotificationPreference: "none",
          handsFreeNotificationPreference: "none",
        },
      ),
    );
  }

  public async subscribeToDriverFeeds({
    workspaceId,
    workspaceMembershipId,
    driverWorkspaceMembershipIds,
  }: {
    workspaceId: string;
    workspaceMembershipId: string;
    driverWorkspaceMembershipIds: string[];
  }) {
    return this.processRequest(
      this.workspacesClient.request({
        path: `/workspaces/${workspaceId}/membership/${workspaceMembershipId}/driver-feed-preferences`,
        method: "POST",
        body: { workspaceMembershipIds: driverWorkspaceMembershipIds },
        type: "application/json",
        format: "json",
      }),
    );
  }

  public async unsubscribeFromDriverFeeds({
    workspaceId,
    workspaceMembershipId,
    driverWorkspaceMembershipIds,
  }: {
    workspaceId: string;
    workspaceMembershipId: string;
    driverWorkspaceMembershipIds: string[];
  }) {
    return this.processRequest(
      this.workspacesClient.request({
        path: `/workspaces/${workspaceId}/membership/${workspaceMembershipId}/driver-feed-preferences`,
        method: "DELETE",
        body: { workspaceMembershipIds: driverWorkspaceMembershipIds },
        type: "application/json",
        format: "json",
      }),
    );
  }

  /////////////////////////////
  // BOOTSTRAP ENDPOINTS
  /////////////////////////////

  public async latestUnreadEventPerFeedForOrganizers({
    workspaceId,
    page,
    pageSize,
    timestamp,
  }: {
    workspaceId: string;
    page?: number;
    pageSize?: number;
    timestamp?: string;
  }) {
    return this.processRequest(
      this.bootstrapClient.workspacesLatestUnreadEventPerFeedForOrganizersDetail(workspaceId, {
        page,
        pageSize,
        timestamp,
      }),
    );
  }

  public async latestUnreadEventPerFeed({
    workspaceId,
    page,
    pageSize,
    timestamp,
  }: {
    workspaceId: string;
    page?: number;
    pageSize?: number;
    timestamp?: string;
  }) {
    return this.processRequest(
      this.bootstrapClient.workspacesLatestUnreadEventPerFeedDetail(workspaceId, {
        page,
        pageSize,
        timestamp,
      }),
    );
  }

  public async bootstrapWorkspaceMemberships({
    workspaceId,
    page,
    pageSize,
    timestamp,
  }: {
    workspaceId: string;
    page?: number;
    pageSize?: number;
    timestamp?: string;
  }) {
    return this.processRequest(
      this.bootstrapClient.workspacesWorkspaceMembershipsDetail(workspaceId, {
        page,
        pageSize,
        timestamp,
      }),
    );
  }

  public async bootstrapWorkspaceFeedPermissions({
    workspaceId,
    page,
    pageSize,
    timestamp,
    mine,
  }: {
    workspaceId: string;
    page?: number;
    pageSize?: number;
    timestamp?: string;
    mine?: boolean;
  }) {
    return this.processRequest(
      this.bootstrapClient.workspacesFeedPermissionsDetail(workspaceId, {
        page,
        pageSize,
        timestamp,
        mine,
      }),
    );
  }

  public async bootstrapWorkspaceFeedItemsPaginated({
    workspaceId,
    page,
    pageSize,
    timestamp,
    feedId,
    sortAscending,
    itemId,
    useCreatedAt,
  }: {
    workspaceId: string;
    page?: number;
    pageSize?: number;
    timestamp?: string;
    feedId?: string;
    sortAscending?: boolean;
    itemId?: string;
    useCreatedAt?: boolean;
  }) {
    return this.processRequest(
      this.bootstrapClient.workspacesFeedItemsDetail(workspaceId, {
        page,
        pageSize,
        timestamp,
        feedId,
        sortAscending,
        itemId,
        useCreatedAt,
      }),
    );
  }

  public async bootstrapWorkspaceFeeds({
    workspaceId,
    page,
    pageSize,
    timestamp,
  }: {
    workspaceId: string;
    page?: number;
    pageSize?: number;
    timestamp?: string;
  }) {
    return this.processRequest(
      this.bootstrapClient.workspacesFeedsDetail(workspaceId, {
        page,
        pageSize,
        timestamp,
      }),
    );
  }

  public async bootstrapWorkspaceMembershipAvailabilities({
    workspaceId,
    page,
    pageSize,
    timestamp,
  }: {
    workspaceId: string;
    page?: number;
    pageSize?: number;
    timestamp?: string;
  }) {
    return this.processRequest(
      this.bootstrapClient.workspacesWorkspaceMembershipAvailabilitiesDetail(workspaceId, {
        page,
        pageSize,
        timestamp,
      }),
    );
  }

  public async bootstrapWorkspaceTemplates({
    workspaceId,
    page,
    pageSize,
    timestamp,
  }: {
    workspaceId: string;
    page?: number;
    pageSize?: number;
    timestamp?: string;
  }) {
    return this.processRequest(
      this.bootstrapClient.workspacesTemplatesDetail(workspaceId, {
        page,
        pageSize,
        timestamp,
      }),
    );
  }

  public async bootstrapWorkspaceAccounts({
    workspaceId,
    page,
    pageSize,
    timestamp,
  }: {
    workspaceId: string;
    page?: number;
    pageSize?: number;
    timestamp?: string;
  }) {
    return this.processRequest(
      this.bootstrapClient.workspacesAccountsDetail(workspaceId, {
        page,
        pageSize,
        timestamp,
      }),
    );
  }

  public async bootstrapWorkspaceCommandAliases({
    workspaceId,
    page,
    pageSize,
    timestamp,
  }: {
    workspaceId: string;
    page?: number;
    pageSize?: number;
    timestamp?: string;
  }) {
    return this.processRequest(
      this.bootstrapClient.workspacesCommandAliasesDetail(workspaceId, {
        page,
        pageSize,
        timestamp,
      }),
    );
  }

  public async bootstrapWorkspaceFeedGroups({
    workspaceId,
    page,
    pageSize,
    timestamp,
  }: {
    workspaceId: string;
    page?: number;
    pageSize?: number;
    timestamp?: string;
  }) {
    return this.processRequest(
      this.bootstrapClient.workspacesFeedGroupsDetail(workspaceId, {
        page,
        pageSize,
        timestamp,
      }),
    );
  }

  public async bootstrapWorkspaceFeedGroupMemberships({
    workspaceId,
    page,
    pageSize,
    timestamp,
  }: {
    workspaceId: string;
    page?: number;
    pageSize?: number;
    timestamp?: string;
  }) {
    return this.processRequest(
      this.bootstrapClient.workspacesFeedGroupMembershipsDetail(workspaceId, {
        page,
        pageSize,
        timestamp,
      }),
    );
  }

  public async bootstrapWorkspaceFeedPreferences({
    workspaceId,
    page,
    pageSize,
    timestamp,
    mine,
    feedId,
  }: {
    workspaceId: string;
    page?: number;
    pageSize?: number;
    timestamp?: string;
    mine?: boolean;
    feedId?: string;
  }) {
    return this.processRequest(
      this.bootstrapClient.workspacesFeedPreferencesDetail(workspaceId, {
        page,
        pageSize,
        timestamp,
        mine,
        feedId,
      }),
    );
  }

  public async bootstrapWorkspaceNonAliasFeedLatestItems({
    workspaceId,
    page,
    pageSize,
    timestamp,
  }: {
    workspaceId: string;
    page?: number;
    pageSize?: number;
    timestamp?: string;
  }) {
    return this.processRequest(
      this.bootstrapClient.workspacesNonAliasFeedLatestItemsDetail(workspaceId, {
        page,
        pageSize,
        timestamp,
      }),
    );
  }

  public async bootstrapWorkspaceAliasFeedLatestOwnerItems({
    workspaceId,
    page,
    pageSize,
    timestamp,
  }: {
    workspaceId: string;
    page?: number;
    pageSize?: number;
    timestamp?: string;
  }) {
    return this.processRequest(
      this.bootstrapClient.workspacesAliasFeedLatestOwnerItemsDetail(workspaceId, {
        page,
        pageSize,
        timestamp,
      }),
    );
  }

  public async bootstrapWorkspaceDriverTranscripts({
    workspaceId,
    language,
    format,
    timestamp,
  }: {
    workspaceId: string;
    language?: string;
    format?: string;
    timestamp?: string;
  }) {
    return this.processRequest(
      this.bootstrapClient.workspacesDriverTranscriptsDetail(workspaceId, {
        language,
        format: format as "Text" | "Html" | "SimpleHtml" | "SSML",
        timestamp,
      }),
    );
  }

  public async bootstrapWorkspaceTags({
    workspaceId,
    page,
    pageSize,
    timestamp,
  }: {
    workspaceId: string;
    page?: number;
    pageSize?: number;
    timestamp?: string;
  }) {
    return this.processRequest(
      this.bootstrapClient.workspacesTagsDetail(workspaceId, {
        page,
        pageSize,
        timestamp,
      }),
    );
  }

  public async bootstrapWorkspaceMembershipTags({
    workspaceId,
    page,
    pageSize,
    timestamp,
  }: {
    workspaceId: string;
    page?: number;
    pageSize?: number;
    timestamp?: string;
  }) {
    return this.processRequest(
      this.bootstrapClient.workspacesWorkspaceMembershipsTagsDetail(workspaceId, {
        page,
        pageSize,
        timestamp,
      }),
    );
  }

  public async createCall({
    callId,
    fromAccountId,
    toAccountId,
    feedId,
  }: {
    callId: string;
    fromAccountId: string;
    toAccountId: string;
    feedId?: string;
  }) {
    return this.processRequest(
      this.callClient.callCreate(callId, {
        fromAccountId,
        toAccountId,
        feedIds: [feedId],
      }),
    );
  }

  public async createCallEvent(
    callId: string,
    { callStatus, durationSeconds }: { callStatus: CallStatus; durationSeconds?: number },
  ) {
    return this.processRequest(this.callClient.callEventCreate(callId, { callStatus, durationSeconds }));
  }

  public async getLocationsFromItemIds({ workspaceId, itemIds }: { workspaceId: string; itemIds: string[] }) {
    return await this.processRequest(this.workspacesClient.feedItemsLocationsCreate(workspaceId, { itemIds }));
  }

  public createPingResponse(request: CreatePingResponse) {
    return this.processRequest(this.deviceClient.pingResponseCreate(request));
  }
}
